import React, { useEffect, useState } from 'react';
import {
  fetchSlashings,
  fetchCompensations,
  fetchProducts,
  type Slashing,
  type Compensation,
  type InsuranceProduct,
} from '../api/insurance';
import { useInsurancePool, useAGUBalance, formatAGU, explorerUrl, OBJECTS } from '../sui';
import { useCurrentAccount } from '@mysten/dapp-kit';

export default function Pool() {
  const [slashings, setSlashings] = useState<Slashing[]>([]);
  const [compensations, setCompensations] = useState<Compensation[]>([]);
  const [products, setProducts] = useState<InsuranceProduct[]>([]);
  const [loading, setLoading] = useState(true);

  const account = useCurrentAccount();
  const { balance } = useAGUBalance();
  const { info: poolOnchain } = useInsurancePool();

  useEffect(() => {
    (async () => {
      try {
        const [sl, comp, prod] = await Promise.all([
          fetchSlashings(),
          fetchCompensations(),
          fetchProducts(),
        ]);
        setSlashings(sl);
        setCompensations(comp);
        setProducts(prod);
      } catch {
        /* keep defaults */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const premiums = products.reduce((s, p) => s + (p.premium || 0), 0);
  const slashedTotal = slashings.reduce((s, sl) => s + (sl.amount || 0), 0);
  const paidTotal = compensations.filter((c) => c.status === 'paid' || c.status === 'approved')
    .reduce((s, c) => s + (c.amount || 0), 0);
  const pendingTotal = compensations.filter((c) => c.status === 'pending')
    .reduce((s, c) => s + (c.amount || 0), 0);
  const poolBalance = premiums + slashedTotal - paidTotal;

  return (
    <div>
      <header className="insurance-header">
        <h1>Insurance Pool</h1>
        <p>Pool balance, slashing history, and fund allocation</p>
      </header>

      {/* ── Pool Status ── */}
      <div className="insurance-card" style={{ textAlign: 'center', marginBottom: 24 }}>
        <span className="insurance-shield-icon" />
        <div className="insurance-stat-label">Total Pool Balance (On-chain)</div>
        <div className="insurance-stat-value safe" style={{ fontSize: 56 }}>
          {formatAGU(poolOnchain.poolBalance)} <span style={{ fontSize: 20 }}>AGU</span>
        </div>
        <div className="insurance-coverage" style={{ maxWidth: 400, margin: '16px auto 0' }}>
          <div className="insurance-coverage-bar">
            <div
              className="insurance-coverage-fill"
              style={{ width: `${premiums + slashedTotal > 0 ? Math.min((poolBalance / (premiums + slashedTotal)) * 100, 100) : 72}%` }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#888' }}>
          <a href={explorerUrl('object', OBJECTS.insurancePool)} target="_blank" rel="noreferrer"
             style={{ color: '#00c878' }}>
            View on SuiScan ↗
          </a>
          {account && <span> • Your balance: {balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} AGU</span>}
        </div>
      </div>

      {/* ── Fund Breakdown ── */}
      <div className="insurance-grid" style={{ marginBottom: 24 }}>
        <div className="insurance-card">
          <div className="insurance-card-title">
            <span className="insurance-icon">💰</span> Premiums Collected
          </div>
          <div className="insurance-stat-value safe">{premiums.toLocaleString()}</div>
          <div className="insurance-stat-label">AGU from {products.length} products</div>
        </div>
        <div className="insurance-card">
          <div className="insurance-card-title">
            <span className="insurance-icon">🔥</span> Slashed Funds
          </div>
          <div className="insurance-stat-value protected">{slashedTotal.toLocaleString()}</div>
          <div className="insurance-stat-label">AGU from {slashings.length} slashings</div>
        </div>
        <div className="insurance-card">
          <div className="insurance-card-title">
            <span className="insurance-icon">💸</span> Compensations Paid
          </div>
          <div className="insurance-stat-value">{paidTotal.toLocaleString()}</div>
          <div className="insurance-stat-label">AGU to claimants</div>
        </div>
        <div className="insurance-card">
          <div className="insurance-card-title">
            <span className="insurance-icon">⏳</span> Pending Claims
          </div>
          <div className="insurance-stat-value protected">{pendingTotal.toLocaleString()}</div>
          <div className="insurance-stat-label">AGU awaiting approval</div>
        </div>
      </div>

      {/* ── Slashing History ── */}
      <div className="insurance-card" style={{ marginBottom: 24 }}>
        <div className="insurance-card-header">
          <span className="insurance-card-title">
            <span className="insurance-icon">🔥</span>
            Slashing History
          </span>
          <span className="insurance-badge warning">{slashings.length}</span>
        </div>
        {loading ? (
          <div className="insurance-loading">Loading…</div>
        ) : slashings.length === 0 ? (
          <div className="insurance-empty">No slashings recorded. All agents compliant.</div>
        ) : (
          <table className="insurance-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Amount</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {slashings.map((s, i) => (
                <tr key={i}>
                  <td className="mono">{s.agentId}</td>
                  <td className="insurance-stat-value protected" style={{ fontSize: 16, margin: 0 }}>
                    {s.amount} AGU
                  </td>
                  <td>{s.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── How Insurance Works ── */}
      <div className="insurance-card">
        <div className="insurance-card-header">
          <span className="insurance-card-title">
            <span className="insurance-icon">📖</span>
            How Insurance Works
          </span>
        </div>
        <div className="insurance-timeline">
          {[
            { step: '1', label: 'Purchase Coverage', desc: 'Task requesters buy insurance products with AGU premiums' },
            { step: '2', label: 'Agent Executes', desc: 'Agent performs task with staked collateral' },
            { step: '3', label: 'Incident Occurs', desc: 'Failure, timeout, or quality issue is detected & reported' },
            { step: '4', label: 'Verification', desc: 'Verifier panel assesses failure severity' },
            { step: '5', label: 'Compensation', desc: 'Pool pays out claimants, agent stake slashed if at fault' },
          ].map((t, i) => (
            <div className="insurance-timeline-step" key={i}>
              <div className="insurance-timeline-marker">{t.step}</div>
              <div>
                <strong>{t.label}</strong>
                <p className="insurance-stat-label">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
